import {
  fetchEventSource,
  EventSourceMessage,
} from "@microsoft/fetch-event-source";
import agentPlatformClient from "./agentPlatformClient";
import { getAgentServiceUrl } from "./axiosClient";
import { currentSession } from "../utils/user-auth-provider";
import { API_ENDPOINTS, ACTION_STATUS } from "../commons/ai-chat.constants";

export type DraftResponsePayload = {
  messages: { role: "user" | "assistant"; content: string }[];
  currentPlatform?: string;
};

export type DraftResponseTask = {
  taskId: string;
  status:
  | typeof ACTION_STATUS.PENDING
  | typeof ACTION_STATUS.COMPLETED
  | typeof ACTION_STATUS.FAILED;
  data?: unknown;
};

export type BrowserPlan = {
  id: string;
  name: string;
  description: string;
  actions?: Array<{
    id: string;
    description: string;
    parameters?: Record<string, any>;
  }>;
};

export type ActionStatus =
  | typeof ACTION_STATUS.PENDING
  | typeof ACTION_STATUS.EXECUTING
  | typeof ACTION_STATUS.COMPLETED
  | typeof ACTION_STATUS.FAILED;

export const ACTION_IDS = {
  SEARCH_USER: "search_user",
  SEARCH_LOAN: "search_loan",
  FORMAT_RESPONSE: "format_response",
  READ_LOAN_APPLICATION: "read_loan_application",
} as const;

/**
 * EventSource-like interface for type compatibility
 */
export interface EventSourceLike {
  onmessage: ((evt: MessageEvent) => void) | null;
  onerror: ((evt: Event) => void) | null;
  onopen: ((evt: Event) => void) | null;
  close: () => void;
  readonly readyState: number;
  readonly url: string;
  readonly CONNECTING: number;
  readonly OPEN: number;
  readonly CLOSED: number;
}

/**
 * EventSource-like wrapper for fetchEventSource
 * Maintains compatible API with native EventSource for backward compatibility
 */
class FetchEventSourceWrapper implements EventSourceLike {
  private abortController: AbortController;

  public onmessage: ((evt: MessageEvent) => void) | null = null;

  public onerror: ((evt: Event) => void) | null = null;

  public onopen: ((evt: Event) => void) | null = null;

  private isClosed = false;

  // EventSource compatibility properties
  public readonly CONNECTING = 0;

  public readonly OPEN = 1;

  public readonly CLOSED = 2;

  public readonly url: string;

  public get readyState(): number {
    return this.isClosed ? this.CLOSED : this.OPEN;
  }

  constructor(url: string, token?: string) {
    this.url = url;
    this.abortController = new AbortController();

    fetchEventSource(url, {
      signal: this.abortController.signal,
      openWhenHidden: true,
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "text/event-stream",
      },
      onopen: async (response) => {
        if (response.ok) {
          // Connection successful
          if (this.onopen) {
            this.onopen(new Event("open"));
          }
        } else if (
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 429
        ) {
          // Client-side error, don't retry
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        } else {
          // Server error or rate limit, will retry
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      },

      onmessage: (evt: EventSourceMessage) => {
        if (this.isClosed) return;

        // Convert EventSourceMessage to MessageEvent format
        const messageEvent = new MessageEvent("message", {
          data: evt.data,
          lastEventId: evt.id || "",
          origin: new URL(url).origin,
        });

        if (this.onmessage) {
          this.onmessage(messageEvent);
        }
      },

      onerror: (err: any) => {
        if (this.isClosed) return;

        // Convert error to Event format
        const errorEvent = new Event("error");
        (errorEvent as any).error = err;

        if (this.onerror) {
          this.onerror(errorEvent);
        }

        // Determine whether this error is retryable based on HTTP status (if available)
        const status = (err && typeof err === "object" && "status" in err)
          ? (err as any).status
          : undefined;
        if (
          typeof status === "number" &&
          status >= 400 &&
          status < 500 &&
          status !== 429
        ) {
          // Non-retryable client error: close connection and stop retries
          this.isClosed = true;
          this.abortController.abort();
          throw err;
        }
      },

      onclose: () => {
        // Connection closed by server, allow retry
        // Do NOT set this.isClosed = true here, as that prevents processing messages after reconnect
      },
    }).catch((err) => {
      // Log errors that occur outside the onerror handler for debugging
      // eslint-disable-next-line no-console
      console.error("FetchEventSourceWrapper initialization error:", err);
    });
  }

  close() {
    this.isClosed = true;
    this.abortController.abort();
    this.onmessage = null;
    this.onerror = null;
    this.onopen = null;
  }
}

export type ChatBlock = {
  block_type: "text";
  text: string;
};

export type LookupKnowledgePayload = {
  chatHistory: {
    role: "user" | "assistant";
    blocks: ChatBlock[];
  }[];
  currentPlatform?: string;
  conversationID?: string;
};

export const createConversation = (agencyId?: string) => {
  return agentPlatformClient.post<{ data: { conversationID: string } }>(
    API_ENDPOINTS.CREATE_CONVERSATION,
    {},
    {
      withCredentials: false,
      headers: agencyId ? { "x-company-id": agencyId } : undefined,
    }
  );
};

export const requestLookupInternalKnowledge = (
  payload: LookupKnowledgePayload,
  agencyId?: string
) => {
  return agentPlatformClient.post<DraftResponseTask>(
    API_ENDPOINTS.LOOKUP_INTERNAL_KNOWLEDGE,
    payload,
    {
      withCredentials: false,
      headers: agencyId ? { "x-company-id": agencyId } : undefined,
    }
  );
};

export const requestDraftResponse = (
  payload: DraftResponsePayload,
  agencyId?: string
) => {
  return agentPlatformClient.post<DraftResponseTask>(
    API_ENDPOINTS.DRAFT_RESPONSE,
    payload,
    {
      withCredentials: false,
      headers: agencyId ? { "x-company-id": agencyId } : undefined,
    }
  );
};

export const createLookupInternalKnowledgeEventSource = async (
  tenantId: string
): Promise<EventSourceLike> => {
  const session = await currentSession();
  const token = session?.tokens?.accessToken?.toString();

  const url = `${getAgentServiceUrl()}${API_ENDPOINTS.LOOKUP_INTERNAL_KNOWLEDGE_SSE(tenantId)}`;

  return new FetchEventSourceWrapper(url, token);
};

export type FormatResponsePayload = {
  planId: string;
  chatHistory: { role: "user" | "assistant"; content: string }[];
  actionResults: Record<string, any>;
  conversationID?: string;
};

export const requestFormatResponse = async (
  payload: FormatResponsePayload,
  agencyId?: string
) => {
  return agentPlatformClient.post(API_ENDPOINTS.FORMAT_RESPONSE, payload, {
    withCredentials: false,
    headers: agencyId ? { "x-company-id": agencyId } : undefined,
  });
};
